import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ITag, ITagType, TagEmptyObject } from "@src/types/tags";
import { ITrack } from "@src/types/track";

type CRUDActionState = {
  tagData: ITag;
  loading: boolean;
  success: boolean;
  error: string;
  modalOpen: boolean;
  tagType: ITagType | undefined;
};
interface TagFactoryState {
  totalTagsCount: number;
  totalTracksCount: number;
  spaceUsedForTracks: number;
  selectedTrack: ITrack | null;
  tagTypeToCreate: ITagType | undefined;
  refetchTagsByTagType: ITagType | undefined;
  isCreateModalOpen: boolean;

  updateTagState: CRUDActionState;
  deleteTagState: CRUDActionState;

  // Filters for untagged tracks
  filters: {
    genre: ITag[];
    emotion: ITag[];
    tags: ITag[];
  };
}
const actionTagStateInitialState: CRUDActionState = {
  tagData: TagEmptyObject,
  loading: false,
  success: false,
  error: "",
  modalOpen: false,
  tagType: undefined,
};
export const initialTagFactoryState: TagFactoryState = {
  totalTagsCount: 0,
  totalTracksCount: 0,
  spaceUsedForTracks: 0,
  selectedTrack: null,
  refetchTagsByTagType: undefined,
  tagTypeToCreate: undefined,
  isCreateModalOpen: false,
  updateTagState: actionTagStateInitialState,
  deleteTagState: actionTagStateInitialState,
  filters: {
    genre: [],
    emotion: [],
    tags: [],
  },
};

const tagFactorySlice = createSlice({
  name: "tagFactory",
  initialState: initialTagFactoryState,
  reducers: {
    setTagsCount(state, action: PayloadAction<number>) {
      state.totalTagsCount = action.payload;
    },
    setTracksCount(state, action: PayloadAction<number>) {
      state.totalTracksCount = action.payload;
    },
    setSpaceUsedForTracks(state, action: PayloadAction<number>) {
      state.spaceUsedForTracks = action.payload;
    },
    setSelectedTrack(state, action: PayloadAction<ITrack | null>) {
      state.selectedTrack = action.payload;
    },
    setTagTypeToCreate(state, action: PayloadAction<ITagType | undefined>) {
      state.tagTypeToCreate = action.payload;
    },
    setCreateTagModalOpen(state, action: PayloadAction<boolean>) {
      state.isCreateModalOpen = action.payload;
    },
    setRefetchTagsByTagType(
      state,
      action: PayloadAction<ITagType | undefined>
    ) {
      state.refetchTagsByTagType = action.payload;
    },
    setUpdateTagState(
      state,
      action: PayloadAction<Partial<TagFactoryState["updateTagState"]>>
    ) {
      state.updateTagState = {
        ...state.updateTagState,
        ...action.payload,
      };
    },
    resetUpdateTagState(state) {
      state.updateTagState = actionTagStateInitialState;
    },
    setDeleteTagState(
      state,
      action: PayloadAction<Partial<TagFactoryState["deleteTagState"]>>
    ) {
      state.deleteTagState = {
        ...state.deleteTagState,
        ...action.payload,
      };
    },
    resetDeleteTagState(state) {
      state.deleteTagState = actionTagStateInitialState;
    },
    setTagFactoryFilters(
      state,
      action: PayloadAction<{
        genre?: ITag[];
        emotion?: ITag[];
        tags?: ITag[];
      }>
    ) {
      if (action.payload.genre !== undefined) {
        state.filters.genre = action.payload.genre;
      }
      if (action.payload.emotion !== undefined) {
        state.filters.emotion = action.payload.emotion;
      }
      if (action.payload.tags !== undefined) {
        state.filters.tags = action.payload.tags;
      }
    },
    resetTagFactoryFilters(state) {
      state.filters = {
        genre: [],
        emotion: [],
        tags: [],
      };
    },
  },
});

export const {
  setTagsCount,
  setTracksCount,
  setSpaceUsedForTracks,
  setSelectedTrack,
  setTagTypeToCreate,
  setCreateTagModalOpen,
  setRefetchTagsByTagType,
  setUpdateTagState,
  resetUpdateTagState,
  setDeleteTagState,
  resetDeleteTagState,
  setTagFactoryFilters,
  resetTagFactoryFilters,
} = tagFactorySlice.actions;

export default tagFactorySlice.reducer;
